import React, { memo } from 'react'
import { Divider, ListSubheader, Menu, MenuItem } from '@mui/material'
import { CheckIcon, MicrophoneIcon, SpeakerWaveIcon, VideoCameraIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'

// ── Constants ─────────────────────────────────────────────────────────────────

const DEVICE_SECTION_HEADER_SX = {
  backgroundColor: 'rgba(0,0,0,0.25)',
  color: INTERAX_COLORS.thunderGrey,
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  py: 1,
  px: 2
}

const MENU_DIVIDER_SX = { borderColor: INTERAX_COLORS.white08, my: 0.5 }

const DEVICE_MENU_PAPER_SX = {
  backgroundColor: INTERAX_COLORS.offBlack,
  border: `1px solid ${INTERAX_COLORS.white15}`,
  borderRadius: '12px',
  minWidth: 240,
  py: 0.5
}

// ── Types ─────────────────────────────────────────────────────────────────────

type Device = { deviceId: string; label: string }

interface DeviceMenuSectionProps {
  SectionIcon: React.ElementType
  label: string
  devices: Device[]
  currentDeviceId: string | undefined
  onSelect: (deviceId: string) => void
}

export interface DeviceMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  microphoneDevices: Device[]
  cameraDevices: Device[]
  speakerDevices: Device[]
  currentMicrophoneDeviceId: string | undefined
  currentCameraDeviceId: string | undefined
  currentSpeakerDeviceId: string | undefined
  onMicrophoneSelect: (deviceId: string) => void
  onCameraSelect: (deviceId: string) => void
  onSpeakerSelect: (deviceId: string) => void
}

// ── DeviceMenuSection ─────────────────────────────────────────────────────────

const DeviceMenuSection = memo(function DeviceMenuSection({
  SectionIcon,
  label,
  devices,
  currentDeviceId,
  onSelect
}: DeviceMenuSectionProps) {
  return (
    <>
      <ListSubheader sx={DEVICE_SECTION_HEADER_SX}>
        <SectionIcon style={{ width: 12, height: 12, flexShrink: 0 }} />
        {label}
      </ListSubheader>
      {devices.map((device) => {
        const isSelected = device.deviceId === currentDeviceId
        return (
          <MenuItem
            key={device.deviceId}
            selected={isSelected}
            onClick={() => onSelect(device.deviceId)}
            sx={{
              color: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
              fontSize: '13px',
              pl: 3.5,
              pr: 2,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              '&.Mui-selected': { backgroundColor: INTERAX_COLORS.sun10 },
              '&:hover': { backgroundColor: INTERAX_COLORS.white10 }
            }}
          >
            {device.label || device.deviceId}
            {isSelected ? (
              <CheckIcon style={{ width: 14, height: 14, flexShrink: 0, color: INTERAX_COLORS.sun }} />
            ) : null}
          </MenuItem>
        )
      })}
    </>
  )
})

// ── DeviceMenu ────────────────────────────────────────────────────────────────

export const DeviceMenu: React.FC<DeviceMenuProps> = ({
  anchorEl,
  onClose,
  microphoneDevices,
  cameraDevices,
  speakerDevices,
  currentMicrophoneDeviceId,
  currentCameraDeviceId,
  currentSpeakerDeviceId,
  onMicrophoneSelect,
  onCameraSelect,
  onSpeakerSelect
}) => {
  const { t } = useTranslation()

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose} PaperProps={{ sx: DEVICE_MENU_PAPER_SX }}>
      <DeviceMenuSection
        SectionIcon={MicrophoneIcon}
        label={t('whereby.devices.microphone')}
        devices={microphoneDevices}
        currentDeviceId={currentMicrophoneDeviceId}
        onSelect={onMicrophoneSelect}
      />
      <Divider sx={MENU_DIVIDER_SX} />
      <DeviceMenuSection
        SectionIcon={VideoCameraIcon}
        label={t('whereby.devices.camera')}
        devices={cameraDevices}
        currentDeviceId={currentCameraDeviceId}
        onSelect={onCameraSelect}
      />
      <Divider sx={MENU_DIVIDER_SX} />
      <DeviceMenuSection
        SectionIcon={SpeakerWaveIcon}
        label={t('whereby.devices.speaker')}
        devices={speakerDevices}
        currentDeviceId={currentSpeakerDeviceId}
        onSelect={onSpeakerSelect}
      />
    </Menu>
  )
}
